/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: DomainAliasesTable.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Table component for displaying and managing domain aliases.
────────────────────────────────────────────────────────────────*/

import { Plus, Edit, Trash2 } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import type { DomainAlias } from '../types';

interface DomainAliasesTableProps {
  aliases: DomainAlias[];
  onAdd: () => void;
  onEdit: (alias: DomainAlias) => void;
  onDelete: (aliasId: number | string) => void;
}

export function DomainAliasesTable({
  aliases,
  onAdd,
  onEdit,
  onDelete
}: DomainAliasesTableProps) {
  const { t } = useI18n();

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-border">
              <th className="py-2 px-3 text-left font-medium text-foreground">
                {t('telephonyDomains.aliases.tableHeaders.alias')}
              </th>
              <th className="py-2 px-3 text-left font-medium text-foreground">
                {t('telephonyDomains.aliases.tableHeaders.description')}
              </th>
              <th className="py-2 px-3 text-left font-medium text-foreground">
                {t('telephonyDomains.aliases.tableHeaders.status')}
              </th>
              <th className="py-2 px-3 text-left font-medium text-foreground">
                {t('telephonyDomains.aliases.tableHeaders.actions')}
              </th>
            </tr>
          </thead>
          <tbody>
            {aliases.length === 0 ? (
              <tr>
                <td colSpan={4} className="py-8 px-3 text-center text-muted-foreground">
                  {t('telephonyDomains.aliases.empty')}
                </td>
              </tr>
            ) : (
              aliases.map(alias => (
                <tr key={alias.id} className="border-b border-border transition-colors hover:bg-accent/50">
                  <td className="py-2 px-3 font-mono text-foreground">{alias.alias}</td>
                  <td className="py-2 px-3 text-muted-foreground">
                    {alias.description || t('telephonyDomains.noDescription')}
                  </td>
                  <td className="py-2 px-3">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-1 text-xs ${alias.enabled
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        }`}
                    >
                      {alias.enabled ? t('telephonyDomains.active') : t('telephonyDomains.inactive')}
                    </span>
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-2">
                      <SimpleTooltip content={t('telephonyDomains.aliases.tooltips.edit')}>
                        <button
                          onClick={() => onEdit(alias)}
                          className="rounded-md p-1.5 text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-700 dark:text-blue-400 dark:hover:bg-blue-900/40 dark:hover:text-blue-200"
                          type="button"
                          aria-label={t('telephonyDomains.aliases.tooltips.edit')}
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      </SimpleTooltip>
                      <SimpleTooltip content={t('telephonyDomains.aliases.tooltips.delete')}>
                        <button
                          onClick={() => alias.id && onDelete(alias.id)}
                          className="rounded-md p-1.5 text-red-600 transition-colors hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-900/40 dark:hover:text-red-200"
                          type="button"
                          aria-label={t('telephonyDomains.aliases.tooltips.delete')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </SimpleTooltip>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={onAdd}
          className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-primary-foreground transition-colors hover:bg-primary/90 text-sm"
        >
          <Plus className="h-4 w-4" />
          {t('telephonyDomains.aliases.addTitle')}
        </button>
      </div>
    </>
  );
}


